import { IPaginationDTO, IRoleDTO, IUserDTO, PaginationDTO, RoleDTO, UserDTO } from '@/dtos';
import {
  registerAPI,
  resetMyPasswordAPI,
  updateMyProfileAPI,
  checkEmailAddressAPI,
  resetForgotPasswordAPI,
} from '@/services';
import {
  adminCheckEmailAPI,
  createUserAPI,
  deleteUserAPI,
  getAllRolesAPI,
  getAllUsersAPI,
  getUserByIdAPI,
  resetUserPasswordAPI,
  saveUserAPI,
} from '@/services/admin';
import { useMutation, useQuery, useQueryClient, UseQueryReturnType } from '@tanstack/vue-query';
import { defineStore } from 'pinia';
import { computed, Ref, ref, watch } from 'vue';
import { useAuthStore, useUpdateStatusStore } from '.';

export const useUserStore = defineStore('user', () => {
  const statusStore = useUpdateStatusStore();
  const queryClient = useQueryClient();
  const pagination = ref<IPaginationDTO>(new PaginationDTO());
  const paginationQueryKey = computed(() => ['email-templates', pagination.value]);
  const authStore = useAuthStore();

  const getAllUsersQuery = useQuery({
    queryKey: paginationQueryKey,
    queryFn: async (): Promise<{ data: IUserDTO[]; count: number }> => {
      const ret = await getAllUsersAPI(pagination.value);
      const users = await Promise.all<IUserDTO[]>(ret.data.map((user) => new UserDTO(user)));
      return { data: users, count: ret.count };
    },
    enabled: computed(() => {
      return !!authStore.isAdmin;
    }),
  });

  const getRolesQuery = useQuery({
    queryKey: ['roles'],
    queryFn: async (): Promise<{ data: IRoleDTO[]; count: number }> => {
      const ret = await getAllRolesAPI();
      const roles = await Promise.all<IRoleDTO[]>(ret.data.map((role) => new RoleDTO(role)));
      return { data: roles, count: ret.count };
    },
    enabled: computed(() => {
      return !!authStore.isAdmin;
    }),
  });

  const getUser = async (userId: string): Promise<IUserDTO> => {
    const user = await getUserByIdAPI(userId);
    return new UserDTO(user);
  };

  const getUserByIdQuery = (userId: Ref<string>) => {
    return useQuery<IUserDTO>({
      queryKey: ['getUserById', userId.value],
      queryFn: async () => {
        const obj = await getUser(userId.value);
        const retobj = new UserDTO(obj);
        return retobj;
      },
      enabled: !!userId.value,
    });
  };

  const checkEmail = (email: string, userId?: string): UseQueryReturnType<boolean, Error> => {
    return useQuery<boolean, Error>({
      queryKey: ['check-email', email, userId],
      queryFn: () => {
        if (authStore.isAdmin && userId) {
          return adminCheckEmailAPI(email, userId);
        } else {
          return checkEmailAddressAPI(email, userId);
        }
      },
      enabled: !!email,
    });
  };

  // Mutations
  const create = useMutation({
    mutationFn: (user: IUserDTO) => {
      if (authStore.isAdmin) {
        return createUserAPI(user);
      } else {
        return registerAPI(user);
      }
    },
    onSuccess: () => {
      const key = `user-create`;
      statusStore.setSuccessStatus(key, 'success:create');
      queryClient.invalidateQueries({ queryKey: paginationQueryKey.value });
    },
    onError: (err: string) => {
      const key = `user-create`;
      statusStore.setErrorStatus(key, err);
    },
  });

  const update = useMutation({
    mutationFn: (user: IUserDTO) => {
      if (authStore.isAdmin) {
        return saveUserAPI(user);
      } else {
        return updateMyProfileAPI(user);
      }
    },
    onSuccess: (data: IUserDTO) => {
      queryClient.invalidateQueries({ queryKey: paginationQueryKey.value });
      queryClient.invalidateQueries({ queryKey: ['getUserById', data.id] });
      if (authStore.me?.id && authStore.me.id == data.id) {
        queryClient.invalidateQueries({ queryKey: ['user-me'] });
      }
      const key = `user-${data.id}`;
      statusStore.setSuccessStatus(key, 'success:update');
    },
    onError: (err: string, variables) => {
      const key = `user-${variables.id}`;
      statusStore.setErrorStatus(key, err);
    },
  });

  const remove = useMutation({
    mutationFn: (userId: string) => deleteUserAPI(userId),
    onSuccess: (data: boolean, userId: string) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: paginationQueryKey.value });
        queryClient.invalidateQueries({ queryKey: ['getUserById', userId] });
        const key = `user-${userId}`;
        statusStore.setSuccessStatus(key, 'success:delete');
      } else {
        throw 'Unable to remove user';
      }
    },
    onError: (err: string, userId) => {
      const key = `user-${userId}`;
      statusStore.setErrorStatus(key, err);
    },
  });

  const resetPassword = useMutation({
    mutationFn: ({ password, userId }: { password: string; userId: string }) => {
      if (authStore.isAdmin) {
        return resetUserPasswordAPI(userId, password);
      } else {
        return resetMyPasswordAPI(password);
      }
    },
    onSuccess: (data: boolean, variables) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['getUserById', variables.userId] });
        if (authStore.me && authStore.me.id == variables.userId) {
          queryClient.invalidateQueries({ queryKey: ['user-me'] });
        }
        const key = `user-delete-${variables.userId}`;
        statusStore.setSuccessStatus(key, 'success:update');
      } else {
        throw 'Could not reset password';
      }
    },
    onError: (err: string, variables) => {
      const key = `user-delete-${variables.userId}`;
      statusStore.setErrorStatus(key, err);
    },
  });

  const forgotPassword = useMutation({
    mutationFn: ({
      code,
      email,
      password,
    }: {
      code: string;
      email: string;
      password: string;
      userId?: string;
    }) => {
      return resetForgotPasswordAPI(code, email, password);
    },
    onSuccess: (data: boolean, variables) => {
      if (data) {
        queryClient.invalidateQueries({ queryKey: ['getUserById', variables.userId] });
        if (authStore.me && authStore.me.id == variables.userId) {
          queryClient.invalidateQueries({ queryKey: ['user-me'] });
        }
        const key = `user-password-${variables.userId}`;
        statusStore.setSuccessStatus(key, 'success:update');
      } else {
        throw 'Could not reset password';
      }
    },
    onError: (err: string, variables) => {
      const key = `user-password-${variables.userId}`;
      statusStore.setErrorStatus(key, err);
    },
  });

  const refetchUser = (userId?: string) => {
    if (userId) {
      queryClient.invalidateQueries({ queryKey: ['getUserById', userId] });
      if (authStore.me?.id && authStore.me.id == userId) {
        queryClient.invalidateQueries({ queryKey: ['user-me'] });
      }
    } else {
      queryClient.invalidateQueries({ queryKey: ['user-me'] });
    }
  };

  watch(pagination, () => {
    getAllUsersQuery.refetch();
  });

  return {
    pagination,
    create,
    update,
    remove,
    getAllUsersQuery,
    getUserByIdQuery,
    getRolesQuery,
    checkEmail,
    resetPassword,
    forgotPassword,
    refetchUser,
  };
});
